import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { VehicleDetail } from '@1po/1po-bff-fe-spec/generated/common/vehicle/VehicleDetail';
import { ROUTER_CATALOG_VEHICLE } from 'app/AppRouter';
import { CarIcon, DotsMoreIcon } from 'assets/icons';
import { DataContainer } from 'components/DataContainer';
import { getLastSearchedVehicleKey } from 'domains/catalog/Catalog.store';
import { VehicleCountry } from 'domains/catalog/Catalog.types';
import { getCatalogSourceUrl } from 'domains/catalog/Catalog.utils';
import { fetchKnownVehicle } from 'domains/catalog/fetchKnownVehicle';
import { fetchRemoveVehicleFromSearchHistoryRequestSaga, getVehicleSearchHistory } from 'domains/user';
import { theme } from 'styles';
import { Box, CenteredSpin, CenterFlex, Flex, GreyButton, Icon, Lowercase, MarginBox, ScalableImage, Text } from 'UI';
import VehicleFlag from 'UI/Icon/VehicleFlag';
import { TextWithPopup } from 'UI/Text/TextWithPopup';
import { useExtraLarge, useLarge } from 'utils';
import { formatVehicleBrand, getVehicleLabel } from 'utils/mappers';
import {
  SItemActions,
  SOptionsButton,
  SOptionsMenu,
  SOptionsMenuItem,
  SSearchHistoryItem,
} from './SearchHistoryList.styled';

function onMenuContainerClick(event: React.MouseEvent) {
  event.stopPropagation();
}

function SearchHistoryItem({
  vehicle,
  openMenuKey,
  setOpenMenuKey,
}: {
  readonly vehicle: VehicleDetail;
  readonly openMenuKey: string | null;
  readonly setOpenMenuKey: (key: string | null) => void;
}) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const history = useHistory();
  const params = new URLSearchParams(location.search);
  const lastSearchedVehicleKey = useSelector(getLastSearchedVehicleKey);
  const vehicleImage = vehicle.imageUrl;
  const isSelected = lastSearchedVehicleKey === vehicle.vehicleKey;
  const xLarge = useExtraLarge();
  const large = useLarge();
  const menuRef = useRef<HTMLDivElement>(null);
  const isMenuOpen = openMenuKey === vehicle.vehicleKey;
  const FR_INSPECTION_CHECKLIST_URL =
    'https://s3.eu-west-1.amazonaws.com/provider.medias.parts-io.com/docs/pdfs/TECHNICALCONTROL_FR_20250317.pdf';

  function selectVehicle() {
    if (vehicle.country) {
      params.set(VehicleCountry, vehicle.country);
    }
    fetchKnownVehicle(dispatch, vehicle);
    history.push(
      `${getCatalogSourceUrl(vehicle.catalogSource)}/${vehicle.vehicleKey}${ROUTER_CATALOG_VEHICLE}?${params}`,
    );
  }

  function removeVehicle() {
    dispatch(
      fetchRemoveVehicleFromSearchHistoryRequestSaga({
        vehicleKey: vehicle.vehicleKey,
      }),
    );
  }

  useEffect(() => {
    const onDocumentClick = (event: MouseEvent) => {
      if (isMenuOpen && menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpenMenuKey(null);
      }
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (isMenuOpen && event.key === 'Escape') {
        setOpenMenuKey(null);
      }
    };

    document.addEventListener('mousedown', onDocumentClick);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocumentClick);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isMenuOpen, setOpenMenuKey]);

  function onOptionsButtonClick() {
    setOpenMenuKey(isMenuOpen ? null : vehicle.vehicleKey);
  }

  function onSelectVehicleClick() {
    selectVehicle();
    setOpenMenuKey(null);
  }

  function onInspectionChecklistClick() {
    window.open(FR_INSPECTION_CHECKLIST_URL, '_blank', 'noopener,noreferrer');
    setOpenMenuKey(null);
  }

  function onRemoveVehicleClick() {
    removeVehicle();
    setOpenMenuKey(null);
  }

  const getBody = () => {
    if (vehicle.catalogSource === 'DATAHUB') {
      return vehicle.dataHubVehicle?.modelType;
    }

    if (vehicle.iamVehicle?.bodyType && vehicle.iamVehicle?.doors) {
      return `${vehicle.iamVehicle?.bodyType} ${
        vehicle.iamVehicle?.doors
          ? t('catalog.iam.vehicle.doors', '{{numberOfDoors}} Doors', {
              numberOfDoors: vehicle.iamVehicle.doors,
            })
          : ''
      }`;
    }
    return undefined;
  };

  const getGearbox = () => {
    if (vehicle.catalogSource === 'DATAHUB') {
      return vehicle.dataHubVehicle?.gearbox;
    }

    const values = [vehicle.iamVehicle?.gearboxCode, vehicle.iamVehicle?.gearboxType, vehicle.iamVehicle?.gearbox];
    const gearboxText = values.filter((a) => a).join(' - ');
    if (gearboxText?.length > 0) {
      return `${gearboxText} - ${t('catalog.vehicle.speed', 'speed')} ${t(
        'catalog.vehicle.gearbox',
        'Gearbox',
      ).toLowerCase()}`;
    }

    return undefined;
  };

  const vehicleLabel = getVehicleLabel(vehicle);
  const body = getBody();
  const gearbox = getGearbox();

  return (
    <SSearchHistoryItem>
      <Flex>
        <Box height={90} width={90}>
          <ScalableImage
            height={90}
            width={90}
            imageUrl={vehicleImage}
            catalogSource={vehicle.catalogSource}
            fallbackSrc={''}
            fallbackComponent={
              <Box height={90} width={90} background={theme.color.grey95}>
                <CenterFlex>
                  <Icon IconComponent={CarIcon} size={40} color={theme.color.brand_black} noPointer />
                </CenterFlex>
              </Box>
            }
            alt={
              vehicle.dataHubVehicle?.name ??
              (`${formatVehicleBrand(vehicle.vehicleBrand) ?? ''} ${vehicle.model ?? ''}`.trim() || 'Vehicle thumbnail')
            }
            cursor={'auto'}
          />
        </Box>
        <MarginBox mr={15} />
        <Flex direction={'column'} align={'flex-start'}>
          <Flex align={'center'}>
            <Text type={'h5_bold'}>{vehicleLabel}</Text>
            <MarginBox mr={10} />
            {vehicle.catalogSource === 'DATAHUB' ? (
              <VehicleFlag vehicle={vehicle} />
            ) : (
              (vehicle.vin || vehicle.vrn) && <VehicleFlag vehicle={vehicle} />
            )}
            <MarginBox mr={10} />
            <Text type="base">
              (
              {vehicle.catalogSource === 'DATAHUB' ? vehicle.dataHubVehicle?.engine : vehicle.iamVehicle?.version ?? ''}
              )
            </Text>
          </Flex>
          <Flex direction={large ? 'row' : 'column'}>
            {vehicle.vin && (
              <Flex direction={'row'}>
                <Flex direction={'column'}>
                  <Text type="base" bold>
                    {t('catalog.vehicle.vin', 'VIN')}
                  </Text>
                  <Text type="base" bold>
                    {t('catalog.vehicle.vrn', 'VRN')}
                  </Text>
                </Flex>
                <MarginBox mr={15} />
                <Flex direction={'column'}>
                  <Text type="base">{vehicle.vin}</Text>
                  <Text type="base">{vehicle.vrn}</Text>
                </Flex>
                <MarginBox mr={30} />
              </Flex>
            )}
            {!vehicle.vin && vehicle.vrn && (
              <Flex direction={'row'}>
                <Flex direction={'column'}>
                  <Text type="base" bold>
                    {t('catalog.vehicle.vrn', 'VRN')}
                  </Text>
                </Flex>
                <MarginBox mr={15} />
                <Flex direction={'column'}>
                  <Text type="base">{vehicle.vrn}</Text>
                </Flex>
                <MarginBox mr={30} />
              </Flex>
            )}
            <Flex direction={'row'}>
              <Flex direction={'column'}>
                {gearbox && (
                  <Text type="base" bold>
                    {t('catalog.vehicle.gearbox', 'Gearbox')}
                  </Text>
                )}
                {body && (
                  <Text type="base" bold>
                    {t('common.body', 'Body')}
                  </Text>
                )}
              </Flex>
              <MarginBox mr={15} />
              <Flex direction={'column'}>
                {gearbox && (
                  <Flex maxWidth={!xLarge && large ? 150 : ''}>
                    <TextWithPopup
                      type={'text_dim'}
                      transform={'capitalize'}
                      label={gearbox.toLowerCase()}
                      narrow={!vehicle.vin && !vehicle.vrn ? false : !xLarge && large}
                    >
                      <Lowercase>{gearbox}</Lowercase>
                    </TextWithPopup>
                  </Flex>
                )}
                {body && (
                  <Text type="base" transform={'capitalize'}>
                    <Lowercase>{body}</Lowercase>
                  </Text>
                )}
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      </Flex>
      <MarginBox mr={15} />
      <SItemActions ref={menuRef} onClick={onMenuContainerClick}>
        <SOptionsButton
          type={'button'}
          aria-label={t('catalog.vehicle.options', 'Vehicle options')}
          aria-expanded={isMenuOpen}
          onClick={onOptionsButtonClick}
        >
          <Icon IconComponent={DotsMoreIcon} size={20} color={theme.color.brand_black} />
        </SOptionsButton>
        {isMenuOpen && (
          <SOptionsMenu role={'menu'}>
            <SOptionsMenuItem type={'button'} role={'menuitem'} onClick={onSelectVehicleClick}>
              {isSelected
                ? t('catalog.vehicle.current_vehicle', 'Current vehicle')
                : t('catalog.parts.action.identify_new_vehicle', 'Identify new vehicle')}
            </SOptionsMenuItem>
            <SOptionsMenuItem type={'button'} role={'menuitem'} onClick={onInspectionChecklistClick}>
              {t('catalog.parts.action.inspection_checklist', 'Inspection checklist')}
            </SOptionsMenuItem>
            <SOptionsMenuItem type={'button'} role={'menuitem'} onClick={onRemoveVehicleClick}>
              {t('catalog.parts.action.remove_from_history', 'Remove from history')}
            </SOptionsMenuItem>
          </SOptionsMenu>
        )}
      </SItemActions>
    </SSearchHistoryItem>
  );
}

export default function SearchHistoryList() {
  const { t } = useTranslation();
  const vehicleSearchHistory = useSelector(getVehicleSearchHistory);
  const [showMore, setShowMore] = useState<boolean>(false);
  const [openMenuKey, setOpenMenuKey] = useState<string | null>(null);

  function onShowMoreClick() {
    setShowMore(true);
  }

  return (
    <DataContainer
      data={vehicleSearchHistory.searchStatus}
      Loading={() => <CenteredSpin size={'large'} />}
      NotFound={() => (
        <Text type="base" bold>
          {t('catalog.history.is_empty', 'Your search history is empty')}
        </Text>
      )}
    >
      <Flex direction={'column'}>
        {vehicleSearchHistory.data?.slice(0, showMore ? 20 : 4).map((v) => (
          <SearchHistoryItem key={v.vehicleKey} vehicle={v} openMenuKey={openMenuKey} setOpenMenuKey={setOpenMenuKey} />
        ))}
        {!showMore && (vehicleSearchHistory.data?.length ?? 0) > 4 && (
          <>
            <Flex justify={'center'}>
              <GreyButton stretch={false} size={'middle'} onClick={onShowMoreClick}>
                {t('common.load_more', 'Load more')}
              </GreyButton>
            </Flex>
            <MarginBox mt={60} />
          </>
        )}
      </Flex>
    </DataContainer>
  );
}
